<template>
  <ContentHeader title="Your Profile" :learn-more-ids no-border>
    <template #actions>
      <BaseButton
        v-if="activeTabId === 'profile'"
        type="button"
        :disabled="isSaveDisabled"
        @click="onSave"
      >
        Save
      </BaseButton>
      <template v-if="activeTabId === 'almMapping'">
        <BaseButton
          v-if="almSingleSelect"
          variant="outlined"
          type="button"
          :disabled="almSaveDisabled"
          @click="onAlmSaveAndBack"
        >
          Save and back to board
        </BaseButton>
        <FeedbackButton
          :disabled="almSaveDisabled"
          success-message="The user mapping has been saved."
          @click="onAlmSave"
        >
          Save
        </FeedbackButton>
      </template>
    </template>
  </ContentHeader>
  <BaseTabs
    :model-value="activeTabId"
    :tabs
    query-param="tab"
    @update:model-value="attemptTabSwitch"
  >
    <template #profile>
      <PageBody>
        <div class="form-container">
          <header class="identity">
            <div class="avatar" aria-hidden="true">
              <img v-if="user.imageUrl" :src="user.imageUrl" alt="user image" />
              <span v-else>{{ initial }}</span>
            </div>
            <div class="identity-body">
              <h2 class="display-name">{{ form.name || "—" }}</h2>
              <p class="display-email">{{ user.email }}</p>
              <span v-if="user.role" class="role-pill">{{ user.role }}</span>
            </div>
            <div v-if="!ssoOnly" class="identity-actions">
              <BaseButton variant="outlined" type="button" @click="resetPassword">
                Change Password
              </BaseButton>
            </div>
          </header>

          <p v-if="resetPasswordRequestComplete" class="reset-success">
            Your request to reset your password was successful. You should receive an e-mail shortly
            with a link to change your password.
          </p>
          <p v-if="resetPasswordServerError" class="reset-error">
            Could not complete the request to reset your password.
          </p>

          <form ref="profileForm" class="fields" novalidate @submit.prevent>
            <div class="field">
              <BaseInput id="profile-name" v-model="form.name" label="Name" :error="nameError" />
            </div>

            <div class="field">
              <BaseInput id="profile-company" label="Company" :model-value="company" disabled />
            </div>

            <div class="field">
              <BaseInput id="profile-role" label="Role" :model-value="user.role" disabled />
            </div>

            <div class="field">
              <BaseInput id="profile-email" label="E-mail" :model-value="user.email" disabled />
            </div>

            <div class="field">
              <BaseSelect
                id="profile-language"
                v-model="form.preferredLanguage"
                label="Language"
                :options="preferredLanguageOptions"
              />
            </div>
          </form>
        </div>
      </PageBody>
    </template>

    <template #almMapping>
      <PageBody>
        <PageLoading v-if="!almMappings">Loading mappings...</PageLoading>
        <template v-else>
          <MessageBox v-if="almLoadFailed" type="warning" class="alm-load-error">
            Problems loading the data, some connections might be missing.
          </MessageBox>
          <UserMapping
            ref="almMapping"
            v-model:saved-mappings="almMappings"
            @dirty-change="almDirty = $event"
            @single-select-ready="almSingleSelect = $event"
          />
        </template>
      </PageBody>
    </template>
  </BaseTabs>

  <UnsavedChangesModal
    :is-open="showUnsavedChangesModal"
    :save-action="saveUnsaved"
    @discard="discardUnsaved"
    @cancel="cancelUnsaved"
  />
</template>

<script>
import { mapState } from "pinia";

import BaseButton from "@/components-ng/BaseButton.vue";
import BaseInput from "@/components-ng/BaseInput.vue";
import BaseSelect from "@/components-ng/BaseSelect.vue";
import BaseTabs from "@/components-ng/BaseTabs.vue";
import ContentHeader from "@/components-ng/ContentHeader.vue";
import FeedbackButton from "@/components-ng/FeedbackButton.vue";
import MessageBox from "@/components-ng/MessageBox.vue";
import PageBody from "@/components-ng/PageBody.vue";
import PageLoading from "@/components-ng/PageLoading.vue";
import UnsavedChangesModal from "@/components-ng/UnsavedChangesModal.vue";
import { CompanyHandler } from "@/handlers/companyHandler";
import { UserHandler } from "@/handlers/userHandler";
import { useAppStore } from "@/stores";
import { useToastStore } from "@/stores/toast";

import UserMapping from "./UserMapping.vue";
import { loadAlmUserMappings } from "./almUserMappings";

export default {
  name: "ProfilePage",
  components: {
    BaseButton,
    BaseInput,
    BaseSelect,
    BaseTabs,
    ContentHeader,
    FeedbackButton,
    MessageBox,
    PageBody,
    PageLoading,
    UnsavedChangesModal,
    UserMapping,
  },
  async beforeRouteLeave() {
    if (!this.isDirty && !this.almDirty) return;
    this.showUnsavedChangesModal = true;
    const continueNavigation = await new Promise((resolve) => {
      this.resolveNavigation = resolve;
    });
    return continueNavigation ? undefined : false;
  },
  setup() {
    return { toastStore: useToastStore() };
  },
  data() {
    return {
      activeTabId: "profile",
      isSaving: false,
      resetPasswordRequestComplete: false,
      resetPasswordServerError: "",
      ssoOnly: true,
      nameErrorVisible: false,
      showUnsavedChangesModal: false,
      resolveNavigation: null,
      almDirty: false,
      almMappings: null,
      almLoadFailed: false,
      almSingleSelect: false,
      pendingTabId: null,
      form: {
        name: "",
        preferredLanguage: "",
      },
    };
  },
  computed: {
    ...mapState(useAppStore, ["isObserver", "company", "session", "user"]),
    hasAlmMapping() {
      return !this.isObserver && (this.almLoadFailed || this.almMappings?.length !== 0);
    },
    tabs() {
      return [
        { id: "profile", label: "Profile" },
        ...(this.hasAlmMapping ? [{ id: "almMapping", label: "ALM Mapping" }] : []),
      ];
    },
    learnMoreIds() {
      return ["user-profile", ...(this.hasAlmMapping ? ["alm-user-mapping"] : [])];
    },
    userHandler() {
      return new UserHandler();
    },
    companyHandler() {
      return new CompanyHandler();
    },
    initial() {
      const source = this.form.name.trim() || this.user?.email || "?";
      return source.substring(0, 1).toUpperCase();
    },
    preferredLanguageOptions() {
      return [
        { value: "en", label: "English" },
        { value: "es", label: "Español" },
        { value: "pt", label: "Português" },
        { value: "ja", label: "日本語" },
        { value: "fr", label: "Français" },
        { value: "de", label: "Deutsch" },
        { value: "zh", label: "简体中文" },
      ];
    },
    nameValid() {
      return this.form.name.trim().length > 0;
    },
    nameError() {
      return this.nameErrorVisible && !this.nameValid ? "Name is required" : "";
    },
    isDirty() {
      return (
        this.form.name !== (this.user.name ?? "") ||
        this.form.preferredLanguage !== (this.user.preferredLanguage ?? "")
      );
    },
    isSaveDisabled() {
      // The button stays enabled when the name is invalid; clicking surfaces the
      // inline error instead of saving. A clean (non-dirty) form or an in-flight
      // save disables it.
      return this.isSaving || !this.isDirty;
    },
    almSaveDisabled() {
      return !this.almDirty;
    },
    isActiveTabDirty() {
      return this.activeTabId === "almMapping" ? this.almDirty : this.isDirty;
    },
  },
  created() {
    this.form.name = this.user.name ?? "";
    this.form.preferredLanguage = this.user.preferredLanguage ?? "";
  },
  async mounted() {
    if (this.user.role === "admin") {
      this.fetchSSOOnly();
    }
    if (this.isObserver) return;
    if (this.$route.query.mapping) {
      this.activeTabId = "almMapping";
    }
    const { mappings, failed } = await loadAlmUserMappings(this.$route.query.mapping);
    this.almMappings = mappings;
    this.almLoadFailed = failed;
    if (!this.hasAlmMapping) {
      this.activeTabId = "profile";
    }
  },
  methods: {
    async fetchSSOOnly() {
      this.ssoOnly = await this.companyHandler.ssoOnlyLogin();
    },

    async resetPassword() {
      if (!this.$refs.profileForm.reportValidity()) return;
      this.resetPasswordServerError = "";
      try {
        await this.userHandler.resetMyPassword();
      } catch (e) {
        this.resetPasswordServerError = e.message || e;
        return;
      }
      this.resetPasswordRequestComplete = true;
      setTimeout(() => (this.resetPasswordRequestComplete = false), 5000);
    },

    // Header Save: button stays enabled; an invalid name surfaces the inline
    // error below the field and skips the save (no toast, no request).
    onSave() {
      if (!this.nameValid) {
        this.nameErrorVisible = true;
        return;
      }
      this.nameErrorVisible = false;
      return this.toastStore.showFeedback({
        action: this.persist(),
        successMessage: "Profile updated.",
      });
    },

    // Persists the draft. Throws on server failure so callers (showFeedback /
    // the unsaved-changes modal) surface the error.
    async persist() {
      // Snapshot the draft so the values we mark as persisted match what we sent,
      // even if the user keeps typing while the request is in flight.
      const draft = {
        name: this.form.name,
        preferredLanguage: this.form.preferredLanguage,
      };
      this.isSaving = true;
      try {
        const response = await this.userHandler.updateMe(draft);
        if (!response.success) {
          throw new Error(`Could not update profile: ${response.error}`);
        }
        this.user.name = draft.name;
        this.user.preferredLanguage = draft.preferredLanguage;
      } finally {
        this.isSaving = false;
      }
    },

    // --- ALM Mapping header Save (delegates to the child via ref) ---
    onAlmSave() {
      return this.$refs.almMapping?.save();
    },
    onAlmSaveAndBack() {
      return this.$refs.almMapping?.saveAndBack();
    },

    // --- Tab switching: guard the active tab if it has unsaved changes ---
    attemptTabSwitch(newId) {
      if (newId === "almMapping") {
        // Drop the previous mount's readiness so the header button can't reuse
        // stale state before the remounted UserMapping emits the current one.
        this.almSingleSelect = false;
      }
      if (!this.isActiveTabDirty) {
        this.activeTabId = newId;
        return;
      }
      this.pendingTabId = newId;
      this.showUnsavedChangesModal = true;
      new Promise((resolve) => {
        this.resolveNavigation = resolve;
      }).then((proceed) => {
        if (proceed) this.activeTabId = this.pendingTabId;
        this.pendingTabId = null;
      });
    },

    // --- Unsaved-changes guard (mirrors AlmArt.vue) ---
    resolveModal(continueNavigation) {
      if (this.resolveNavigation) {
        this.resolveNavigation(continueNavigation);
        this.resolveNavigation = null;
      }
    },
    resetActiveTab() {
      if (this.activeTabId === "almMapping") {
        // UserMapping unmounts when we switch away, so its state resets itself —
        // clearing the parent flag is enough to mark the tab clean.
        this.almDirty = false;
      } else {
        this.form.name = this.user.name ?? "";
        this.form.preferredLanguage = this.user.preferredLanguage ?? "";
        this.nameErrorVisible = false;
      }
    },
    cancelUnsaved() {
      // Stay put.
      this.showUnsavedChangesModal = false;
      this.resolveModal(false);
    },
    discardUnsaved() {
      // Proceed without saving — revert the active tab's draft.
      this.showUnsavedChangesModal = false;
      this.resetActiveTab();
      this.resolveModal(true);
    },
    async saveUnsaved() {
      // Save the active tab, then proceed. Errors are surfaced by the modal's
      // FeedbackButton (this is its save-action) and cancel the navigation.
      if (this.activeTabId === "almMapping") {
        try {
          await this.onAlmSave();
          this.resolveModal(true);
        } catch (error) {
          this.resolveModal(false);
          throw error;
        } finally {
          this.showUnsavedChangesModal = false;
        }
        return;
      }
      // Profile tab: an invalid name blocks navigation and shows the inline error.
      if (!this.nameValid) {
        this.nameErrorVisible = true;
        this.showUnsavedChangesModal = false;
        this.resolveModal(false);
        throw new Error("Name is required");
      }
      try {
        await this.persist();
        this.resolveModal(true);
      } catch (error) {
        this.resolveModal(false);
        throw error;
      } finally {
        this.showUnsavedChangesModal = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@use "@/colors" as c;

.form-container {
  max-width: 640px;
}

.alm-load-error {
  max-width: 720px;
  margin-bottom: 20px;
}

.identity {
  display: flex;
  gap: 20px;
  align-items: center;
  padding-bottom: 24px;
  margin-bottom: 24px;
  border-bottom: 1px solid c.$border-subtle;
}

.identity-actions {
  margin-left: auto;
  flex-shrink: 0;
}

.avatar {
  flex-shrink: 0;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
  background: linear-gradient(135deg, c.$main 0%, c.$main-hover 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: c.$text-inverse;
  font-size: 2rem;
  font-weight: 600;
  letter-spacing: -0.02em;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.identity-body {
  flex: 1;
  min-width: 0;
}

.display-name {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: c.$text;
  line-height: 1.2;
}

.display-email {
  margin: 4px 0 0;
  font-size: 0.8125rem;
  color: c.$text-subtle;
}

.role-pill {
  display: inline-block;
  margin-top: 8px;
  padding: 2px 10px;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: c.$main;
  background: c.$background-grey-light;
  border-radius: 999px;
}

.fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px 24px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.field:first-child {
  grid-column: 1 / -1;
}

.reset-success {
  margin: 0 0 16px;
  font-size: 0.75rem;
  color: c.$brand-success;
}

.reset-error {
  margin: 0 0 16px;
  font-size: 0.75rem;
  color: c.$brand-danger;
}
</style>
